import React, { useContext, useEffect, useRef, useState } from "react";
import {
    Navigate,
    useLocation,
    useNavigate,
    useSearchParams,
} from "react-router-dom";

import { post, get } from "../../controlers/global/ConfigRequest";
import {
    COMPANY_REGISTER_ADDRESS,
    COMPANY_REGISTER_TYPE_LIST,
    LOGIN_ADDRESS,
    REGISTER_ADDRESS,
    SEND_OTP,
} from "../../helperData/ApiAddress";
import {
    applyTokenResponse,
    getDefaultPanelPath,
    getLoginErrorMessage,
    isSafeReturnPath,
    isAuthenticated,
    resolvePostLoginPath,
} from "../../helperData/panelRoutes";
import {
    persistAppFlavor,
    resolveAppFlavor,
    getPostLoginFlavor,
    APP_FLAVOR_ORGAN,
} from "../../helperData/appFlavor";
import { GlobalStateContext } from "../../layouts/GlobalStates";
import { useTranslation } from "react-i18next";
import "./style.scss";

const OTP_LENGTH = 5;
const OTP_TIMEOUT = 120;
const USERNAME_PATTERN = /^[A-Za-z0-9._@+-]+$/;
const PHONE_LIKE_USERNAME = /^(?:\+98|0098|98|0)?9\d{9}$/;

const normalizeDigits = (value = "") =>
    value
        .replace(/[۰-۹]/g, (digit) => "۰۱۲۳۴۵۶۷۸۹".indexOf(digit))
        .replace(/[٠-٩]/g, (digit) => "٠١٢٣٤٥٦٧٨٩".indexOf(digit));

const normalizePhone = (value) => {
    const phone = normalizeDigits(value).replace(/[\s-]/g, "");
    if (/^09\d{9}$/.test(phone)) {
        return `+98${phone.slice(1)}`;
    }
    if (/^\+989\d{9}$/.test(phone)) {
        return phone;
    }
    return null;
};

const getRequestError = (error, fallback) => {
    const data = error?.response?.data;
    if (data?.phone_number) {
        return "این شماره همراه قبلاً ثبت شده است. وارد حساب خود شوید.";
    }
    if (data?.message === "User not found.") {
        return "کاربری با این شماره پیدا نشد. ابتدا ثبت نام کنید.";
    }
    if (Array.isArray(data?.non_field_errors)) {
        return data.non_field_errors[0];
    }
    if (typeof data?.detail === "string") {
        return data.detail;
    }
    return fallback;
};

const readApiFieldError = (value) => {
    if (Array.isArray(value) && value[0]) return String(value[0]);
    if (typeof value === "string") return value;
    return "";
};

function AuthBrandLink({ className }) {
    return (
        <a className={className} href="/" aria-label="فیدیما Feedima">
            <svg
                className="auth-brand-mark"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="146 2 72 60"
                width="44"
                height="38"
                aria-hidden="true"
                focusable="false"
            >
                <path d="M153 28C156.5 14.5 168.5 5 183.5 5c14.8 0 27.2 9.5 30.7 23.2a7 7 0 0 1-13.6 3.4c-1.9-7.4-8.7-12.6-17.1-12.6-8.2 0-15 5-17 12.3l5.2 6.2h-12.5c-4.2 0-7.3-4.8-6.2-9.5Z" />
                <path d="M215 36c-3.5 13.5-15.5 23-30.5 23-14.8 0-27.2-9.5-30.7-23.2a7 7 0 0 1 13.6-3.4c1.9 7.4 8.7 12.6 17.1 12.6 8.2 0 15-5 17-12.3l-5.2-6.2h12.5c4.2 0 7.3 4.8 6.2 9.5Z" />
                <path
                    d="m184 22 2.7 7.3L194 32l-7.3 2.7L184 42l-2.7-7.3L174 32l7.3-2.7L184 22Z"
                    opacity=".55"
                />
            </svg>
            <span className="auth-wordmark">
                <span className="auth-wordmark__fa" lang="fa">
                    فیدیما
                </span>
                <span className="auth-wordmark__en" lang="en">
                    Feedima
                </span>
            </span>
        </a>
    );
}

const Field = ({
    id,
    label,
    error,
    hint,
    children,
    optional = false,
}) => (
    <div className={`auth-field${error ? " auth-field--error" : ""}`}>
        <div className="auth-field__label-row">
            <label htmlFor={id}>{label}</label>
            {optional && <span>اختیاری</span>}
        </div>
        {children}
        {hint && !error && <p className="auth-field__hint">{hint}</p>}
        {error && (
            <p className="auth-field__error" id={`${id}-error`}>
                {error}
            </p>
        )}
    </div>
);

const BusinessCategorySelect = ({
    categories,
    loading,
    value,
    onChange,
}) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const selected = categories.find(
        (category) => String(category.id) === String(value)
    );

    useEffect(() => {
        const closeOnOutsideClick = (event) => {
            if (!rootRef.current?.contains(event.target)) {
                setOpen(false);
            }
        };
        const closeOnEscape = (event) => {
            if (event.key === "Escape") {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", closeOnOutsideClick);
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            document.removeEventListener("mousedown", closeOnOutsideClick);
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, []);

    return (
        <div className="auth-select" ref={rootRef}>
            <button
                id="business-category"
                className="auth-select__trigger"
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => !loading && setOpen((current) => !current)}
                disabled={loading}
            >
                <span>
                    {loading
                        ? "در حال دریافت دسته‌بندی‌ها…"
                        : selected?.name || "انتخاب دسته‌بندی"}
                </span>
                <span className="auth-select__chevron" aria-hidden="true">
                    ⌄
                </span>
            </button>
            {open && (
                <div
                    className="auth-select__menu"
                    role="listbox"
                    aria-label="دسته‌بندی کسب و کار"
                >
                    <button
                        type="button"
                        role="option"
                        aria-selected={!value}
                        onClick={() => {
                            onChange("");
                            setOpen(false);
                        }}
                    >
                        بدون دسته‌بندی
                    </button>
                    {categories.map((category) => (
                        <button
                            key={category.id}
                            type="button"
                            role="option"
                            aria-selected={
                                String(category.id) === String(value)
                            }
                            onClick={() => {
                                onChange(category.id);
                                setOpen(false);
                            }}
                        >
                            {category.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const AuthPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        auth,
        setAuth,
        setCurrentPanel,
        setRefreshNewCount,
    } = useContext(GlobalStateContext);

    const { t } = useTranslation();
    const view =
        searchParams.get("view") === "register" ? "register" : "login";
    const loginMethod =
        searchParams.get("method") === "password" ? "password" : "otp";
    const returnPath = searchParams.get("next") || location.state?.from;
    const appFlavor = resolveAppFlavor({
        pathname: location.pathname,
        search: location.search,
    });

    const [step, setStep] = useState("phone");
    const [phone, setPhone] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showRegisterPassword, setShowRegisterPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);
    const [otp, setOtp] = useState("");
    const [otpTimer, setOtpTimer] = useState(0);
    const [debugCode, setDebugCode] = useState("");
    const [notice, setNotice] = useState("");
    const [requestError, setRequestError] = useState("");
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [registration, setRegistration] = useState({
        firstName: "",
        lastName: "",
        phone: "",
        wantsCredentials: false,
        username: "",
        password: "",
        passwordConfirm: "",
        isOrganization: false,
        organizationName: "",
        category: "",
    });
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);

    useEffect(() => {
        persistAppFlavor(appFlavor);
    }, [appFlavor]);

    useEffect(() => {
        if (otpTimer <= 0) return undefined;
        const timeout = window.setTimeout(
            () => setOtpTimer((current) => current - 1),
            1000
        );
        return () => window.clearTimeout(timeout);
    }, [otpTimer]);

    useEffect(() => {
        if (
            view !== "register" ||
            !registration.isOrganization ||
            categories.length > 0 ||
            categoriesLoading
        ) {
            return;
        }
        setCategoriesLoading(true);
        get(COMPANY_REGISTER_TYPE_LIST, { language: "fa" })
            .then((response) => setCategories(response || []))
            .catch(() => setCategories([]))
            .finally(() => setCategoriesLoading(false));
    }, [
        categories.length,
        categoriesLoading,
        registration.isOrganization,
        view,
    ]);

    if (isAuthenticated(auth)) {
        const destination = isSafeReturnPath(returnPath)
            ? returnPath
            : getDefaultPanelPath(auth, appFlavor);
        return <Navigate to={destination} replace />;
    }

    const updateQuery = (nextView, nextMethod = loginMethod) => {
        const next = new URLSearchParams();
        if (nextView === "register") next.set("view", "register");
        if (nextView === "login" && nextMethod === "password") {
            next.set("method", "password");
        }
        if (isSafeReturnPath(searchParams.get("next"))) {
            next.set("next", searchParams.get("next"));
        }
        const appQuery = searchParams.get("app");
        if (appQuery === "organ" || appQuery === "user") {
            next.set("app", appQuery);
        }
        setSearchParams(next, { replace: true });
        setStep("phone");
        setPhone("");
        setPassword("");
        setOtp("");
        setDebugCode("");
        setNotice("");
        setRequestError("");
        setErrors({});
    };

    const focusFirstError = (nextErrors) => {
        const firstId = Object.keys(nextErrors)[0];
        if (!firstId) return;
        window.requestAnimationFrame(() => {
            document.getElementById(firstId)?.focus();
        });
    };

    const completeLogin = (response) => {
        applyTokenResponse(setAuth, response);
        if (!rememberMe) {
            window.localStorage.removeItem("refresh_token");
        }
        setRefreshNewCount((current) => !current);
        const flavor = getPostLoginFlavor(response.user, appFlavor);
        persistAppFlavor(flavor);
        setCurrentPanel(flavor);
        navigate(resolvePostLoginPath(response.user, returnPath, flavor), {
            replace: true,
        });
    };

    const requestOtp = async (phoneValue, successMessage) => {
        const normalizedPhone = normalizePhone(phoneValue);
        if (!normalizedPhone) {
            const nextErrors = {
                phone: "شماره همراه را به شکل 09123456789 وارد کنید.",
            };
            setErrors(nextErrors);
            focusFirstError(nextErrors);
            return;
        }

        setLoading(true);
        setErrors({});
        setRequestError("");
        try {
            const response = await post(SEND_OTP, {
                phone_number: normalizedPhone,
            });
            setPhone(phoneValue);
            setStep("verify");
            setOtp("");
            setOtpTimer(OTP_TIMEOUT);
            setDebugCode(response?.otp_debug_code || "");
            setNotice(successMessage);
        } catch (error) {
            setRequestError(
                getRequestError(
                    error,
                    "ارسال کد انجام نشد. اتصال اینترنت را بررسی و دوباره تلاش کنید."
                )
            );
        } finally {
            setLoading(false);
        }
    };

    const handleOtpRequest = (event) => {
        event.preventDefault();
        requestOtp(phone, "کد تایید برای شماره همراه شما ارسال شد.");
    };

    const handleOtpLogin = async (event) => {
        event.preventDefault();
        const normalizedPhone =
            normalizePhone(phone) || normalizePhone(registration.phone);
        const normalizedOtp = normalizeDigits(otp);
        if (normalizedOtp.length !== OTP_LENGTH) {
            const nextErrors = {
                otp: "کد تایید 5 رقمی را کامل وارد کنید.",
            };
            setErrors(nextErrors);
            focusFirstError(nextErrors);
            return;
        }

        setLoading(true);
        setErrors({});
        setRequestError("");
        try {
            const response = await post(LOGIN_ADDRESS, {
                username: normalizedPhone,
                password: normalizedOtp,
                grant_type: "password",
            });
            completeLogin(response);
        } catch (error) {
            setRequestError(
                getLoginErrorMessage(
                    error,
                    "کد تایید درست نیست یا اعتبار آن تمام شده است. کد جدید بگیرید."
                )
            );
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordLogin = async (event) => {
        event.preventDefault();
        const normalizedUsername = phone.trim();
        const nextErrors = {};
        if (!normalizedUsername) {
            nextErrors.phone = "نام کاربری را وارد کنید.";
        }
        if (!password) {
            nextErrors.password = "رمز عبور خود را وارد کنید.";
        }
        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors);
            focusFirstError(nextErrors);
            return;
        }

        setLoading(true);
        setErrors({});
        setRequestError("");
        try {
            const response = await post(LOGIN_ADDRESS, {
                username: normalizeDigits(normalizedUsername),
                password,
                grant_type: "password",
            });
            completeLogin(response);
        } catch (error) {
            setRequestError(
                getLoginErrorMessage(
                    error,
                    "نام کاربری یا رمز عبور درست نیست."
                )
            );
        } finally {
            setLoading(false);
        }
    };

    const updateRegistration = (key, value) => {
        setRegistration((current) => ({ ...current, [key]: value }));
        setErrors((current) => {
            const next = { ...current, [key]: "" };
            if (key === "password") next.registerPassword = "";
            if (key === "password" || key === "passwordConfirm") {
                next.passwordConfirm = "";
            }
            if (key === "wantsCredentials" && !value) {
                next.username = "";
                next.registerPassword = "";
                next.passwordConfirm = "";
            }
            return next;
        });
        setRequestError("");
    };

    const handleRegister = async (event) => {
        event.preventDefault();
        const normalizedPhone = normalizePhone(registration.phone);
        const nextErrors = {};
        if (!registration.firstName.trim()) {
            nextErrors.firstName = "نام را وارد کنید.";
        }
        if (!registration.lastName.trim()) {
            nextErrors.lastName = "نام خانوادگی را وارد کنید.";
        }
        if (!normalizedPhone) {
            nextErrors.registrationPhone =
                "شماره همراه را به شکل 09123456789 وارد کنید.";
        }
        if (
            registration.isOrganization &&
            !registration.organizationName.trim()
        ) {
            nextErrors.organizationName = "نام سازمان را وارد کنید.";
        }
        if (registration.wantsCredentials) {
            const username = registration.username.trim();
            if (!username) {
                nextErrors.username = "نام کاربری را وارد کنید.";
            } else if (
                username.length < 3 ||
                username.length > 150 ||
                !USERNAME_PATTERN.test(username)
            ) {
                nextErrors.username =
                    "نام کاربری باید ۳ تا ۱۵۰ نویسه و فقط شامل حروف انگلیسی، عدد و . _ @ + - باشد.";
            } else if (PHONE_LIKE_USERNAME.test(username)) {
                nextErrors.username = "نام کاربری نمی‌تواند شماره همراه باشد.";
            }
            if (!registration.password) {
                nextErrors.registerPassword = "رمز عبور را وارد کنید.";
            } else if (registration.password.length < 8) {
                nextErrors.registerPassword =
                    "رمز عبور باید حداقل ۸ نویسه باشد.";
            }
            if (registration.password !== registration.passwordConfirm) {
                nextErrors.passwordConfirm =
                    "تکرار رمز عبور با رمز عبور یکسان نیست.";
            }
        }
        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors);
            focusFirstError(nextErrors);
            return;
        }

        const user = {
            first_name: registration.firstName.trim(),
            last_name: registration.lastName.trim(),
            phone_number: normalizedPhone,
            ...(registration.wantsCredentials
                ? {
                      username: registration.username.trim(),
                      password: registration.password,
                  }
                : {}),
        };
        const payload = registration.isOrganization
            ? {
                  user,
                  company: {
                      name: registration.organizationName.trim(),
                      ...(registration.category
                          ? { category: registration.category }
                          : {}),
                  },
              }
            : user;

        setLoading(true);
        setErrors({});
        setRequestError("");
        try {
            const response = await post(
                registration.isOrganization
                    ? COMPANY_REGISTER_ADDRESS
                    : REGISTER_ADDRESS,
                payload
            );
            setPhone(registration.phone);
            setStep("verify");
            setOtp("");
            setOtpTimer(OTP_TIMEOUT);
            setDebugCode(response?.otp_debug_code || "");
            setNotice(
                "حساب شما ساخته شد. برای تکمیل ثبت نام، شماره همراه را تایید کنید."
            );
        } catch (error) {
            const data = error?.response?.data || {};
            const nextErrors = {};
            const fieldMap = {
                first_name: "firstName",
                last_name: "lastName",
                phone_number: "registrationPhone",
                username: "username",
                password: "registerPassword",
                name: "organizationName",
            };
            Object.entries(fieldMap).forEach(([apiKey, formKey]) => {
                const message = readApiFieldError(data[apiKey]);
                if (message) nextErrors[formKey] = message;
            });
            if (data.phone_number) {
                nextErrors.registrationPhone =
                    "این شماره همراه قبلاً ثبت شده است. وارد حساب خود شوید.";
            }
            if (Object.keys(nextErrors).length > 0) {
                setErrors(nextErrors);
                focusFirstError(nextErrors);
            }
            setRequestError(
                Object.keys(nextErrors).length > 0
                    ? ""
                    : getRequestError(
                          error,
                          "ثبت نام انجام نشد. اطلاعات را بررسی و دوباره تلاش کنید."
                      )
            );
        } finally {
            setLoading(false);
        }
    };

    const handleResend = () => {
        if (otpTimer > 0 || loading) return;
        requestOtp(
            phone || registration.phone,
            "کد تایید جدید برای شما ارسال شد."
        );
    };

    const verificationPanel = (
        <form className="auth-form" onSubmit={handleOtpLogin} noValidate>
            <div className="auth-form__heading">
                <h1>تایید شماره همراه</h1>
                <p>
                    کد ارسال‌شده به{" "}
                    <bdi dir="ltr">
                        {phone || registration.phone}
                    </bdi>{" "}
                    را وارد کنید.
                </p>
            </div>
            {notice && (
                <div className="auth-notice" role="status">
                    {notice}
                </div>
            )}
            {debugCode && (
                <div className="auth-debug-code" role="status">
                    <span>کد محیط توسعه</span>
                    <strong dir="ltr">{debugCode}</strong>
                </div>
            )}
            <Field id="otp" label="کد تایید" error={errors.otp}>
                <input
                    id="otp"
                    name="otp"
                    className="auth-input auth-input--otp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    spellCheck={false}
                    maxLength={OTP_LENGTH}
                    value={otp}
                    aria-invalid={Boolean(errors.otp)}
                    aria-describedby={errors.otp ? "otp-error" : undefined}
                    onChange={(event) => {
                        const value = normalizeDigits(event.target.value);
                        if (/^\d*$/.test(value)) {
                            setOtp(value);
                            setErrors({});
                            setRequestError("");
                        }
                    }}
                />
            </Field>
            <div className="auth-form__inline-actions">
                <button
                    type="button"
                    className="auth-text-button"
                    onClick={handleResend}
                    disabled={otpTimer > 0 || loading}
                >
                    {otpTimer > 0
                        ? `ارسال دوباره تا ${otpTimer} ثانیه`
                        : "ارسال دوباره کد"}
                </button>
                <button
                    type="button"
                    className="auth-text-button"
                    onClick={() => {
                        setStep("phone");
                        setOtp("");
                        setDebugCode("");
                        setNotice("");
                        if (view === "register") {
                            updateQuery("login", "otp");
                        }
                    }}
                >
                    ویرایش شماره
                </button>
            </div>
            {requestError && (
                <div className="auth-error-banner" role="alert">
                    {requestError}
                </div>
            )}
            <button
                className="auth-primary-button"
                type="submit"
                disabled={loading}
            >
                {loading ? "در حال بررسی…" : "تایید و ورود"}
            </button>
        </form>
    );

    return (
        <main className="auth-page">
            <a className="auth-skip-link" href="#auth-form-panel">
                رفتن به فرم
            </a>
            <section
                className="auth-brand-panel"
                aria-label={
                    appFlavor === APP_FLAVOR_ORGAN
                        ? t("AppNav.Organ.Name")
                        : t("AppNav.User.Name")
                }
            >
                <AuthBrandLink className="auth-brand" />
                <div className="auth-brand-panel__content">
                    <p className="auth-brand-panel__kicker">
                        {appFlavor === APP_FLAVOR_ORGAN
                            ? t("Auth.App.Organ.Kicker")
                            : t("Auth.App.User.Kicker")}
                    </p>
                    <h2>
                        {appFlavor === APP_FLAVOR_ORGAN
                            ? t("Auth.App.Organ.Title")
                            : t("Auth.App.User.Title")}
                    </h2>
                    <p>
                        {appFlavor === APP_FLAVOR_ORGAN
                            ? t("Auth.App.Organ.Body")
                            : t("Auth.App.User.Body")}
                    </p>
                    <ul>
                        <li>ورود سریع با کد تایید</li>
                        <li>
                            {appFlavor === APP_FLAVOR_ORGAN
                                ? "پیام‌ها، نمای کلی، تجربه و مشارکت"
                                : "پیام‌ها، مشارکت، اسکن، نظرسنجی و امتیاز"}
                        </li>
                        <li>دسترسی هماهنگ در وب و اپ</li>
                    </ul>
                </div>
                <a className="auth-back-link" href="/">
                    بازگشت به صفحه اصلی
                </a>
            </section>

            <section
                className="auth-form-panel"
                id="auth-form-panel"
                aria-label="ورود و ثبت نام"
            >
                <div className="auth-mobile-header">
                    <AuthBrandLink />
                </div>
                <div className="auth-card">
                    {step !== "verify" && (
                        <div
                            className="auth-view-switch"
                            role="tablist"
                            aria-label="انتخاب ورود یا ثبت نام"
                        >
                            <button
                                type="button"
                                role="tab"
                                aria-selected={view === "login"}
                                onClick={() => updateQuery("login", "otp")}
                            >
                                ورود
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={view === "register"}
                                onClick={() => updateQuery("register")}
                            >
                                کاربر جدید هستم
                            </button>
                        </div>
                    )}

                    {step === "verify" && verificationPanel}

                    {step !== "verify" && view === "login" && (
                        <form
                            className="auth-form"
                            onSubmit={
                                loginMethod === "otp"
                                    ? handleOtpRequest
                                    : handlePasswordLogin
                            }
                            noValidate
                        >
                            <div className="auth-form__heading">
                                <h1>خوش آمدید</h1>
                                <p>
                                    {loginMethod === "password"
                                        ? "نام کاربری و رمز عبور خود را وارد کنید تا وارد حساب کاربری شوید."
                                        : "شماره همراه خود را وارد کنید تا وارد حساب کاربری شوید."}
                                </p>
                            </div>
                            <div
                                className="auth-method-switch"
                                aria-label="روش ورود"
                            >
                                <button
                                    type="button"
                                    aria-pressed={loginMethod === "otp"}
                                    onClick={() => updateQuery("login", "otp")}
                                >
                                    کد تایید
                                </button>
                                <button
                                    type="button"
                                    aria-pressed={loginMethod === "password"}
                                    onClick={() =>
                                        updateQuery("login", "password")
                                    }
                                >
                                    رمز عبور
                                </button>
                            </div>
                            <Field
                                id="phone"
                                label={
                                    loginMethod === "password"
                                        ? "نام کاربری"
                                        : "شماره همراه"
                                }
                                error={errors.phone}
                                hint={
                                    loginMethod === "password"
                                        ? "حروف و عدد مجاز است"
                                        : "مثال: 09123456789"
                                }
                            >
                                <input
                                    id="phone"
                                    name="phone"
                                    className="auth-input"
                                    type={
                                        loginMethod === "password"
                                            ? "text"
                                            : "tel"
                                    }
                                    inputMode={
                                        loginMethod === "password"
                                            ? "text"
                                            : "tel"
                                    }
                                    autoComplete={
                                        loginMethod === "password"
                                            ? "username"
                                            : "tel"
                                    }
                                    dir="ltr"
                                    placeholder={
                                        loginMethod === "password"
                                            ? "username"
                                            : "09123456789"
                                    }
                                    value={phone}
                                    aria-invalid={Boolean(errors.phone)}
                                    aria-describedby={
                                        errors.phone
                                            ? "phone-error"
                                            : undefined
                                    }
                                    onChange={(event) => {
                                        const raw = event.target.value;
                                        if (loginMethod === "password") {
                                            const value = normalizeDigits(raw);
                                            if (
                                                /^[A-Za-z0-9._@+-]*$/.test(value)
                                            ) {
                                                setPhone(value);
                                                setErrors({});
                                                setRequestError("");
                                            }
                                            return;
                                        }
                                        const value = normalizeDigits(raw);
                                        if (/^[+\d\s-]*$/.test(value)) {
                                            setPhone(value);
                                            setErrors({});
                                            setRequestError("");
                                        }
                                    }}
                                />
                            </Field>

                            {loginMethod === "password" && (
                                <>
                                    <Field
                                        id="password"
                                        label="رمز عبور"
                                        error={errors.password}
                                    >
                                        <div className="auth-password-input">
                                            <input
                                                id="password"
                                                name="password"
                                                className="auth-input"
                                                type={
                                                    showPassword
                                                        ? "text"
                                                        : "password"
                                                }
                                                autoComplete="current-password"
                                                value={password}
                                                aria-invalid={Boolean(
                                                    errors.password
                                                )}
                                                aria-describedby={
                                                    errors.password
                                                        ? "password-error"
                                                        : undefined
                                                }
                                                onChange={(event) => {
                                                    setPassword(
                                                        event.target.value
                                                    );
                                                    setErrors({});
                                                    setRequestError("");
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setShowPassword(
                                                        (current) => !current
                                                    )
                                                }
                                            >
                                                {showPassword
                                                    ? "پنهان"
                                                    : "نمایش"}
                                            </button>
                                        </div>
                                    </Field>
                                    <label className="auth-checkbox">
                                        <input
                                            type="checkbox"
                                            name="rememberMe"
                                            checked={rememberMe}
                                            onChange={(event) =>
                                                setRememberMe(
                                                    event.target.checked
                                                )
                                            }
                                        />
                                        <span className="auth-checkbox__mark" />
                                        <span>در این دستگاه بمانم</span>
                                    </label>
                                </>
                            )}

                            {requestError && (
                                <div
                                    className="auth-error-banner"
                                    role="alert"
                                >
                                    {requestError}
                                </div>
                            )}
                            <button
                                className="auth-primary-button"
                                type="submit"
                                disabled={loading}
                            >
                                {loading
                                    ? "لطفاً صبر کنید…"
                                    : loginMethod === "otp"
                                    ? "دریافت کد تایید"
                                    : "ورود به حساب"}
                            </button>
                        </form>
                    )}

                    {step !== "verify" && view === "register" && (
                        <form
                            className="auth-form auth-form--register"
                            onSubmit={handleRegister}
                            noValidate
                        >
                            <div className="auth-form__heading">
                                <h1>ساخت حساب جدید</h1>
                                <p>
                                    اطلاعات پایه را وارد کنید. ثبت نام کمتر از
                                    یک دقیقه زمان می‌برد.
                                </p>
                            </div>
                            <div className="auth-fields-grid">
                                <Field
                                    id="firstName"
                                    label="نام"
                                    error={errors.firstName}
                                >
                                    <input
                                        id="firstName"
                                        name="firstName"
                                        className="auth-input"
                                        type="text"
                                        autoComplete="given-name"
                                        value={registration.firstName}
                                        aria-invalid={Boolean(errors.firstName)}
                                        aria-describedby={
                                            errors.firstName
                                                ? "firstName-error"
                                                : undefined
                                        }
                                        onChange={(event) =>
                                            updateRegistration(
                                                "firstName",
                                                event.target.value
                                            )
                                        }
                                    />
                                </Field>
                                <Field
                                    id="lastName"
                                    label="نام خانوادگی"
                                    error={errors.lastName}
                                >
                                    <input
                                        id="lastName"
                                        name="lastName"
                                        className="auth-input"
                                        type="text"
                                        autoComplete="family-name"
                                        value={registration.lastName}
                                        aria-invalid={Boolean(errors.lastName)}
                                        aria-describedby={
                                            errors.lastName
                                                ? "lastName-error"
                                                : undefined
                                        }
                                        onChange={(event) =>
                                            updateRegistration(
                                                "lastName",
                                                event.target.value
                                            )
                                        }
                                    />
                                </Field>
                            </div>
                            <Field
                                id="registrationPhone"
                                label="شماره همراه"
                                error={errors.registrationPhone}
                                hint="کد تایید به این شماره ارسال می‌شود."
                            >
                                <input
                                    id="registrationPhone"
                                    name="registrationPhone"
                                    className="auth-input"
                                    type="tel"
                                    inputMode="tel"
                                    autoComplete="tel"
                                    dir="ltr"
                                    placeholder="09123456789"
                                    value={registration.phone}
                                    aria-invalid={Boolean(
                                        errors.registrationPhone
                                    )}
                                    aria-describedby={
                                        errors.registrationPhone
                                            ? "registrationPhone-error"
                                            : undefined
                                    }
                                    onChange={(event) => {
                                        const value = normalizeDigits(
                                            event.target.value
                                        );
                                        if (/^[+\d\s-]*$/.test(value)) {
                                            updateRegistration("phone", value);
                                        }
                                    }}
                                />
                            </Field>
                            <label className="auth-checkbox auth-checkbox--organization">
                                <input
                                    type="checkbox"
                                    name="wantsCredentials"
                                    checked={registration.wantsCredentials}
                                    onChange={(event) =>
                                        updateRegistration(
                                            "wantsCredentials",
                                            event.target.checked
                                        )
                                    }
                                />
                                <span className="auth-checkbox__mark" />
                                <span>
                                    <strong>
                                        نام کاربری و رمز عبور هم تعیین کنم
                                    </strong>
                                    <small>
                                        بعداً می‌توانید بدون کد تایید، با همین
                                        نام کاربری و رمز وارد شوید.
                                    </small>
                                </span>
                            </label>
                            {registration.wantsCredentials && (
                                <div className="auth-credential-fields">
                                    <Field
                                        id="username"
                                        label="نام کاربری"
                                        error={errors.username}
                                        hint="حروف انگلیسی، عدد و . _ @ + -"
                                    >
                                        <input
                                            id="username"
                                            name="username"
                                            className="auth-input"
                                            type="text"
                                            autoComplete="username"
                                            dir="ltr"
                                            spellCheck={false}
                                            placeholder="username"
                                            value={registration.username}
                                            aria-invalid={Boolean(
                                                errors.username
                                            )}
                                            aria-describedby={
                                                errors.username
                                                    ? "username-error"
                                                    : undefined
                                            }
                                            onChange={(event) => {
                                                const value = normalizeDigits(
                                                    event.target.value
                                                ).trim();
                                                if (
                                                    USERNAME_PATTERN.test(
                                                        value
                                                    ) ||
                                                    value === ""
                                                ) {
                                                    updateRegistration(
                                                        "username",
                                                        value
                                                    );
                                                }
                                            }}
                                        />
                                    </Field>
                                    <Field
                                        id="registerPassword"
                                        label="رمز عبور"
                                        error={errors.registerPassword}
                                        hint="حداقل ۸ نویسه"
                                    >
                                        <div className="auth-password-input">
                                            <input
                                                id="registerPassword"
                                                name="registerPassword"
                                                className="auth-input"
                                                type={
                                                    showRegisterPassword
                                                        ? "text"
                                                        : "password"
                                                }
                                                autoComplete="new-password"
                                                dir="ltr"
                                                value={registration.password}
                                                aria-invalid={Boolean(
                                                    errors.registerPassword
                                                )}
                                                aria-describedby={
                                                    errors.registerPassword
                                                        ? "registerPassword-error"
                                                        : undefined
                                                }
                                                onChange={(event) =>
                                                    updateRegistration(
                                                        "password",
                                                        event.target.value
                                                    )
                                                }
                                            />
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setShowRegisterPassword(
                                                        (current) => !current
                                                    )
                                                }
                                            >
                                                {showRegisterPassword
                                                    ? "پنهان"
                                                    : "نمایش"}
                                            </button>
                                        </div>
                                    </Field>
                                    <Field
                                        id="passwordConfirm"
                                        label="تکرار رمز عبور"
                                        error={errors.passwordConfirm}
                                    >
                                        <input
                                            id="passwordConfirm"
                                            name="passwordConfirm"
                                            className="auth-input"
                                            type={
                                                showRegisterPassword
                                                    ? "text"
                                                    : "password"
                                            }
                                            autoComplete="new-password"
                                            dir="ltr"
                                            value={registration.passwordConfirm}
                                            aria-invalid={Boolean(
                                                errors.passwordConfirm
                                            )}
                                            aria-describedby={
                                                errors.passwordConfirm
                                                    ? "passwordConfirm-error"
                                                    : undefined
                                            }
                                            onChange={(event) =>
                                                updateRegistration(
                                                    "passwordConfirm",
                                                    event.target.value
                                                )
                                            }
                                        />
                                    </Field>
                                </div>
                            )}
                            <label className="auth-checkbox auth-checkbox--organization">
                                <input
                                    type="checkbox"
                                    name="isOrganization"
                                    checked={registration.isOrganization}
                                    onChange={(event) =>
                                        updateRegistration(
                                            "isOrganization",
                                            event.target.checked
                                        )
                                    }
                                />
                                <span className="auth-checkbox__mark" />
                                <span>
                                    <strong>کاربر سازمانی هستم</strong>
                                    <small>
                                        برای مدیریت بازخوردها و مشتریان سازمان
                                    </small>
                                </span>
                            </label>

                            {registration.isOrganization && (
                                <div className="auth-organization-fields">
                                    <Field
                                        id="organizationName"
                                        label="نام سازمان"
                                        error={errors.organizationName}
                                    >
                                        <input
                                            id="organizationName"
                                            name="organizationName"
                                            className="auth-input"
                                            type="text"
                                            autoComplete="organization"
                                            value={
                                                registration.organizationName
                                            }
                                            aria-invalid={Boolean(
                                                errors.organizationName
                                            )}
                                            aria-describedby={
                                                errors.organizationName
                                                    ? "organizationName-error"
                                                    : undefined
                                            }
                                            onChange={(event) =>
                                                updateRegistration(
                                                    "organizationName",
                                                    event.target.value
                                                )
                                            }
                                        />
                                    </Field>
                                    <Field
                                        id="business-category"
                                        label="دسته‌بندی کسب و کار"
                                        optional
                                    >
                                        <BusinessCategorySelect
                                            categories={categories}
                                            loading={categoriesLoading}
                                            value={registration.category}
                                            onChange={(value) =>
                                                updateRegistration(
                                                    "category",
                                                    value
                                                )
                                            }
                                        />
                                    </Field>
                                </div>
                            )}

                            {requestError && (
                                <div
                                    className="auth-error-banner"
                                    role="alert"
                                >
                                    {requestError}
                                </div>
                            )}
                            <button
                                className="auth-primary-button"
                                type="submit"
                                disabled={loading}
                            >
                                {loading
                                    ? "در حال ساخت حساب…"
                                    : "ساخت حساب و دریافت کد"}
                            </button>
                            <p className="auth-legal">
                                با ثبت نام، قوانین استفاده و حریم خصوصی یورنک را
                                می‌پذیرید.
                            </p>
                        </form>
                    )}
                </div>
            </section>
        </main>
    );
};

export default AuthPage;
